'use client';

import { type ReactNode } from 'react';

import { Typography } from '../../../components/typography/Typography';
import { type Component, type ComponentProps } from '../../../types/component';
import { tv } from '../../../utils/tv';

const formLabelClasses = {
  root: 'zds-form-label-root',
  label: 'zds-form-label-label',
  labelText: 'zds-form-label-text',
  subLabel: 'zds-form-label-sub-label',
  requiredIndicator: 'zds-form-label-required-indicator',
  endLabel: 'zds-form-label-end-label',
} as const;

const formLabelStyles = tv({
  slots: {
    root: [
      formLabelClasses.root,
      'z--form-label-margin-bottom flex items-end z--form-label-gap z--form-label-padding-right z--form-label-padding-left font-medium',
    ],
    label: [formLabelClasses.label, 'flex-1'],
    labelText: [formLabelClasses.labelText],
    // Left margin (not flex gap) so sub-label and asterisk wrap naturally with the label text.
    subLabel: [formLabelClasses.subLabel, 'ml-[var(--z--form-label-gap)]'],
    requiredIndicator: [
      formLabelClasses.requiredIndicator,
      'ml-[var(--z--form-label-gap)]',
    ],
    endLabel: [formLabelClasses.endLabel, 'font-normal shrink-0'],
  },
  variants: {
    color: {
      primary: {
        subLabel: 'text-secondary',
        endLabel: 'text-secondary',
        requiredIndicator: 'text-error-main',
      },
      white: {},
    },
    isDisabled: {
      true: {
        root: 'disabled-foreground',
        subLabel: 'disabled-foreground',
        requiredIndicator: 'disabled-foreground',
        endLabel: 'disabled-foreground',
      },
    },
  },
});

interface FormLabelSlotProps {
  /** Additional props for the `<label>` element (e.g. `htmlFor` from React Aria). */
  label?: ComponentProps<object, 'label'>;
  /** Additional props for the label text wrapper `<span>`. */
  labelText?: ComponentProps<object, 'span'>;
  /** Additional props for the sub-label `<span>`. */
  subLabel?: ComponentProps<object, 'span'>;
  /** Additional props for the required indicator `<span>`. */
  requiredIndicator?: ComponentProps<object, 'span'>;
  /** Additional props for the end label `<span>`. */
  endLabel?: ComponentProps<object, 'span'>;
}

interface FormLabelBaseProps {
  /** The label content. */
  label?: ReactNode;
  /** Secondary content displayed after the label (e.g., "Optional"). Requires `label` to be set. */
  subLabel?: ReactNode;
  /** Whether the field is required. Shows a critical mark (asterisk). */
  isRequired?: boolean;
  /** Whether the field is disabled. */
  isDisabled?: boolean;
  /** Content at the end of the label row (e.g., character count). Rendered outside the `<label>` element to avoid polluting the accessible name. Requires `label` to be set. */
  endLabel?: ReactNode;
  /** Props for internal sub-elements. */
  slotProps?: FormLabelSlotProps;
  /**
   * The colour scheme of the label.
   * @default 'primary'
   */
  color?: 'primary' | 'white';
}

type FormLabelProps = ComponentProps<FormLabelBaseProps, 'div'>;

type FormLabelComponent = Component<FormLabelBaseProps, 'div'>;

const FormLabel: FormLabelComponent = (props) => {
  const {
    label,
    subLabel,
    isRequired,
    isDisabled,
    endLabel,
    slotProps,
    color = 'primary',
    className,
    ref,
    ...rest
  } = props;

  const styles = formLabelStyles({ color, isDisabled });

  // Only render when label text exists. endLabel alone without label text
  // would render as an orphaned element in an otherwise empty label row.
  if (!label) {
    return null;
  }

  return (
    <Typography
      component="div"
      variant="subtitle2"
      {...rest}
      ref={ref}
      className={styles.root({ className })}
    >
      <label
        {...slotProps?.label}
        className={styles.label({
          className: slotProps?.label?.className,
        })}
      >
        <Typography
          {...slotProps?.labelText}
          component="span"
          variant="inherit"
          color="inherit"
          className={styles.labelText({
            className: slotProps?.labelText?.className,
          })}
        >
          {label}
          {subLabel && (
            <Typography
              {...slotProps?.subLabel}
              component="span"
              variant="body2"
              color="inherit"
              className={styles.subLabel({
                className: slotProps?.subLabel?.className,
              })}
            >
              {subLabel}
            </Typography>
          )}
          {isRequired && (
            <Typography
              {...slotProps?.requiredIndicator}
              component="span"
              variant="body2"
              color="inherit"
              className={styles.requiredIndicator({
                className: slotProps?.requiredIndicator?.className,
              })}
              aria-hidden="true"
            >
              *
            </Typography>
          )}
        </Typography>
      </label>
      {endLabel && (
        <Typography
          {...slotProps?.endLabel}
          component="span"
          variant="body2"
          color="inherit"
          className={styles.endLabel({
            className: slotProps?.endLabel?.className,
          })}
        >
          {endLabel}
        </Typography>
      )}
    </Typography>
  );
};

FormLabel.displayName = 'FormLabel';

export {
  FormLabel,
  type FormLabelProps,
  type FormLabelBaseProps,
  type FormLabelSlotProps,
  formLabelClasses,
};
